import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import ScrollNav from "../shared/ScrollNav";
import ScrollToTop from "../shared/ScrollToTop";
import AskAuriWidget from "../shared/AskAuriWidget";
import VoiceAuri from "../voice/VoiceAuri";
import { captureRefFromUrl } from "../../lib/referral";

export default function PublicLayout() {
  const location = useLocation();

  // Capture ?ref=CODE on every route change so an attribution link works
  // regardless of which page the partner shares.
  useEffect(() => { captureRefFromUrl(); }, [location.pathname, location.search]);

  // Scroll-fade-in reveal: attach IntersectionObserver to any `.reveal-on-scroll`
  // element and add `.is-visible` when it enters the viewport. Bulletproof:
  //   1. Reveal immediately anything already visible (fixes deep-links + reloads
  //      that land the user past a section — else that section stays opacity:0).
  //   2. MutationObserver catches late-mounting nodes (e.g. FeaturedOpportunities
  //      arriving after its API call resolves).
  //   3. 2.5s safety-net sweep force-reveals anything still hidden regardless of
  //      viewport position — page NEVER stays blank due to a missed observer.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reveal = (el) => {
      if (!el.classList.contains("is-visible")) el.classList.add("is-visible");
    };
    const hasIO = "IntersectionObserver" in window;
    const io = hasIO
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                reveal(entry.target);
                io.unobserve(entry.target);
              }
            }
          },
          { root: null, threshold: 0.05, rootMargin: "0px 0px -4% 0px" },
        )
      : null;

    const observeAll = () => {
      const nodes = document.querySelectorAll(".reveal-on-scroll:not(.is-visible)");
      nodes.forEach((el) => {
        // Instant reveal for anything already on/past the viewport.
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.98) {
          reveal(el);
          return;
        }
        if (io) io.observe(el);
        else reveal(el); // No IO support → just show it.
      });
    };
    const initialTimer = window.setTimeout(observeAll, 40);

    // Catch elements that mount later (e.g. after an API resolves).
    const mo = new MutationObserver(() => observeAll());
    mo.observe(document.body, { childList: true, subtree: true });

    // Safety net: after 2.5s, whatever is still hidden gets shown — a page
    // section should never remain blank. This is the guardrail that fixes the
    // "blank white viewport" bug reported in production.
    const safetyTimer = window.setTimeout(() => {
      document.querySelectorAll(".reveal-on-scroll:not(.is-visible)").forEach(reveal);
    }, 2500);

    return () => {
      window.clearTimeout(initialTimer);
      window.clearTimeout(safetyTimer);
      mo.disconnect();
      if (io) io.disconnect();
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white text-altura-charcoal" data-scope="public">
      <ScrollToTop />
      <PublicHeader />
      <main className="pt-24">
        <Outlet />
      </main>
      <PublicFooter />
      <ScrollNav />
      <AskAuriWidget />
      <VoiceAuri />
    </div>
  );
}
